/**
 * WGTor Component - Working Group Terms of Reference Display - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * Structured display component for working group Terms of Reference (ToR) content.
 * Filters and categorizes ToR data by working group, displaying header descriptions
 * and key objectives in organized sections with proper semantic HTML structure.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.currentGroup - Current working group data object
 * @param {string|number} props.currentGroup.id - Unique working group identifier
 * @param {string} props.currentGroup.workingGroupName - Name of the working group
 * @param {Array} props.torData - Array of Terms of Reference data objects
 * @param {string|number} props.torData[].id - Unique identifier for ToR entry
 * @param {string|number} props.torData[].workingGroupId - Working group ID reference
 * @param {string} props.torData[].workingGroupName - Working group name reference
 * @param {string} props.torData[].category - Category type ("Header" or "Objective")
 * @param {string} props.torData[].torDescription - ToR description content
 * @returns {JSX.Element} The WGTor component
 * 
 * @example
 * <WGTor 
 *   currentGroup={{id: 1, workingGroupName: "Policy Group"}} 
 *   torData={termsOfReferenceArray} 
 * />
 */

import React from 'react';
import './WGTor.css';  

function WGTor({ currentGroup, torData }) {
 
  // Filter TOR entries for this working group
  const groupTor = torData?.filter(tor => 
    tor.workingGroupId === currentGroup.id || 
    tor.workingGroupName === currentGroup.workingGroupName
  ) || [];

  // Separate entries by category
  const header = groupTor.find(tor => tor.category === "Header")?.torDescription;
  const objectives = groupTor.filter(tor => tor.category === "Objective");

  return (
    <div className="tor-container">
      {/* Show "no results" message when no ToR data found */}
      {groupTor.length === 0 ? (
        <div className="no-results">
          <div className="no-results-content">
            <i className="fas fa-file-alt"></i>
            <h3>Awaiting Update from Working Group</h3>
          </div>
        </div>
      ) : (
        <>
          {/* Header Section */}
          {header && (
            <div className="tor-section">
              <p className="tor-description">
                {header}
              </p>
            </div>
          )}

          {/* Objectives Section */}
          {objectives.length > 0 && (
            <div className="tor-section">
              <h3 className="tor-heading">Key Objectives</h3>
              <ul className="tor-list">
                {objectives.map((objective, index) => (
                  <li key={objective.id || index} className="tor-objective">
                    {objective.torDescription}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default WGTor;

